import { useId, useRef, useState } from 'react';
import { useT } from '../../i18n';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';

export interface CalendarPopoverProps {
  /** Currently selected date (YYYY-MM-DD); seeds the visible month. */
  selectedDate: string;
  /** Today's date (YYYY-MM-DD); days before it are disabled. */
  todayIso: string;
  /** Optional booking-window length in days; days past today+maxDays-1 are disabled. */
  maxDays?: number | null;
  /** Formats a Date to a local YYYY-MM-DD string (never toISOString — see HANDOVER §13). */
  toLocalIso: (dt: Date) => string;
  /** Called with the picked ISO date. */
  onPick: (iso: string) => void;
  /** Closes the popover without picking. */
  onClose: () => void;
}

/** Month-grid date picker popover (Norwegian month/weekday labels); extracted verbatim from the monolith (legacy/App.jsx). */
export function CalendarPopover({
  selectedDate,
  todayIso,
  maxDays = null,
  toLocalIso,
  onPick,
  onClose,
}: CalendarPopoverProps) {
  const t = useT();
  const titleId = safeId('calendar-title', useId());
  // Focus opens on the chosen day, where a keyboard user wants to start —
  // unless it cannot take focus (disabled), when `Dialog` falls back to the
  // first control.
  const selectedRef = useRef<HTMLButtonElement | null>(null);
  const initial = new Date(selectedDate + 'T00:00:00');
  const [viewYear, setViewYear] = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth()); // 0-indexed

  // DEVIATION (lint): renamed `today` → `_today` — it was declared-but-unused in
  // the original monolith (dead code); the `^_` prefix satisfies no-unused-vars
  // without changing behaviour. Otherwise byte-identical.
  const _today = new Date(todayIso + 'T00:00:00');
  let maxIso = null;
  if (maxDays != null) {
    const m = new Date(todayIso + 'T00:00:00');
    m.setDate(m.getDate() + (maxDays - 1));
    maxIso = toLocalIso(m);
  }

  const monthNames = [
    'Januar',
    'Februar',
    'Mars',
    'April',
    'Mai',
    'Juni',
    'Juli',
    'August',
    'September',
    'Oktober',
    'November',
    'Desember',
  ];
  const firstOfMonth = new Date(viewYear, viewMonth, 1);
  // Weekday for the 1st (Mon=0 .. Sun=6)
  const startWeekday = (firstOfMonth.getDay() + 6) % 7;
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewYear(viewYear - 1);
      setViewMonth(11);
    } else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewYear(viewYear + 1);
      setViewMonth(0);
    } else setViewMonth(viewMonth + 1);
  };

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  // A `Dialog` (#900): it dims the page and owns the keyboard while open, so it
  // is modal in all but markup. Named by the month it shows; Escape and the
  // backdrop close it as the backdrop did; focus goes back to the date strip.
  return (
    <Dialog
      labelledBy={titleId}
      onClose={onClose}
      showClose={false}
      initialFocus={selectedRef}
      backdropClassName="fixed inset-0 flex items-start justify-center"
      backdropStyle={{
        zIndex: 60,
        padding: '12px',
        paddingTop: 'max(12vh, 64px)',
        background: 'rgba(15,22,17,0.45)',
      }}
      className="bg-white rounded-2xl shadow-xl border border-line w-[236px] overflow-y-auto max-h-[92vh]"
      style={{ maxHeight: 'calc(100dvh - 2rem)', padding: '10px' }}
    >
      {/* Month nav */}
      <div className="flex items-center justify-between mb-1.5">
        <button
          onClick={prevMonth}
          aria-label={t('booking.calendar.prevMonth')}
          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-bg-soft text-ink-2"
        >
          ‹
        </button>
        <span id={titleId} className="font-display text-[13px] font-semibold text-ink-1">
          {monthNames[viewMonth]} {viewYear}
        </span>
        <button
          onClick={nextMonth}
          aria-label={t('booking.calendar.nextMonth')}
          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-bg-soft text-ink-2"
        >
          ›
        </button>
      </div>
      {/* Weekday row */}
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {['M', 'T', 'O', 'T', 'F', 'L', 'S'].map((w, i) => (
          <div key={i} className="text-center text-[9px] font-mono text-ink-3 py-0.5">
            {w}
          </div>
        ))}
      </div>
      {/* Days */}
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((d, i) => {
          if (d == null) return <div key={i} />;
          const iso = toLocalIso(new Date(viewYear, viewMonth, d));
          const isSel = iso === selectedDate;
          const isToday = iso === todayIso;
          const disabled = iso < todayIso || (maxIso && iso > maxIso);
          return (
            <button
              key={i}
              onClick={() => {
                if (!disabled) onPick(iso);
              }}
              disabled={disabled}
              ref={isSel && !disabled ? selectedRef : undefined}
              aria-pressed={isSel}
              aria-current={isToday ? 'date' : undefined}
              className={`h-7 rounded-lg text-[11px] font-semibold transition-colors ${
                disabled
                  ? 'text-ink-3 font-normal cursor-not-allowed'
                  : isSel
                    ? 'bg-emerald-700 text-white'
                    : isToday
                      ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                      : 'text-ink-1 hover:bg-bg-soft'
              }`}
            >
              {d}
            </button>
          );
        })}
      </div>
      <button
        onClick={onClose}
        className="w-full mt-1.5 text-[11px] text-ink-3 hover:text-ink-1 py-1"
      >
        Lukk
      </button>
    </Dialog>
  );
}
